export type AuthMethod = 'passkey' | 'password';

export type PasskeyHintStatus = 'present' | 'absent' | 'unknown';

export type AuthFallbackReason =
  | 'no_credential'
  | 'sdk_error'
  | 'mw_error'
  | 'launch_window'
  | 'user_cancel'
  | 'check_error';

export type EvaluateAuthMethodInput = {
  skipPasskeyThisSession: boolean;
  isPasskeyLaunchEnabled: boolean;
  isPasskeySupported: boolean;
  passkeyHint: PasskeyHintStatus;
  /** When true, skip passkey and use password. */
  bypassPasskey?: boolean;
};

export type SignInPasskeyFlags = {
  passkey?: boolean;
  passkey_start?: string;
  passkey_end?: string;
} | null;

/**
 * Hardcoded hierarchy: Passkey → Password.
 * Negative cache skips passkey. Unknown and present both attempt passkey.
 */
export const evaluateAuthMethod = (
  input: EvaluateAuthMethodInput
): AuthMethod => {
  const canTryPasskey =
    !input.bypassPasskey &&
    !input.skipPasskeyThisSession &&
    input.isPasskeyLaunchEnabled &&
    input.isPasskeySupported &&
    input.passkeyHint !== 'absent';

  if (canTryPasskey) {
    return 'passkey';
  }
  return 'password';
};

/** Missing flag = allowed. `passkey === false` or outside optional dates = off. */
export const isPasskeyLaunchWindowActive = (
  signInFlags?: SignInPasskeyFlags,
  now = Date.now()
): boolean => {
  if (signInFlags?.passkey === false) {
    return false;
  }
  const start = signInFlags?.passkey_start;
  const end = signInFlags?.passkey_end;
  if (start) {
    const startMs = Date.parse(start);
    if (!Number.isNaN(startMs) && now < startMs) {
      return false;
    }
  }
  if (end) {
    const endMs = Date.parse(end);
    if (!Number.isNaN(endMs) && now > endMs) {
      return false;
    }
  }
  return true;
};

/** MW errors that must not fall through to password. */
export const isPasskeyTerminalMwError = (
  error: { errCode?: string | null } | null | undefined
): boolean =>
  error?.errCode === 'ERR_401_ACCOUNT_LOCKED' ||
  error?.errCode === 'ERR_404_ACC_NOT_ACCESS' ||
  error?.errCode === 'COORP_401_USER_ERROR';
